/**
 * Table selection and phase gating for apnea training.
 *
 * The NAUI tolerance tables (data/nauiTables.ts) are a fixed 8-phase ladder. The
 * manual's "Static%" column is relative to a 2:00 table reference, NOT to the
 * diver's personal maximum — so a phase cannot be chosen safely without also
 * checking it against a measured max static. That check lives here.
 *
 * Gating differs by table type, because the two tables treat "max" differently:
 *
 *  CO2 — the hold is constant across all 8 sets, so it is capped at a fraction of
 *        measured max (CO2_HOLD_FRACTION_CEILING). CO2 work is meant to be
 *        submaximal; the discomfort is the stimulus, not the duration.
 *
 *  O2  — the table deliberately runs past its reference (phase 1 spans 50%-125%),
 *        so a fraction-of-max cap would lock a 2:00 diver out of the phase built
 *        for a 2:00 diver. Entry is instead gated on measured max reaching the
 *        phase's 100% row. See phaseEntrySeconds.
 *
 * Neither gate needs a certification level to be correct, and both degrade safely:
 * an untested user cannot get past the early phases at all.
 */

import {
  ApneaTableType,
  NAUI_PHASES,
  NauiTableSet,
  getNauiTable,
  maxHoldSeconds,
} from '../data/nauiTables';
import { estimateMaxStaticFromBolt } from '../data/apneaProtocols';

/**
 * CO2 tables are meant to be submaximal — the discomfort, not the hold length, is
 * the stimulus. The hold is constant across all 8 sets, so capping it against
 * measured max is both meaningful and safe.
 */
export const CO2_HOLD_FRACTION_CEILING = 0.75;

/**
 * O2 phase entry is gated differently, and the reason is worth recording.
 *
 * NAUI's O2 tables deliberately run PAST the reference max: phase 1 spans 50% to
 * 125% of its 2:00 reference. So the reference is a table label, not a personal
 * ceiling — and capping the longest hold at a fraction of measured max would
 * block every phase for the very divers each phase is written for (a 2:00 diver
 * would be locked out of phase 1, which is built around 2:00).
 *
 * Instead a phase is entered when the diver's measured max reaches that phase's
 * 100% row — the set the table itself treats as "your max". The final sets then
 * exceed measured max by design, which is what an O2 progression is for.
 *
 * This is why the ceiling applies to CO2 only. O2's protection is different:
 * generous fixed 2:00 recovery between sets, a required CO2 base before any O2
 * work at all, and abort-on-symptom.
 */
export const O2_REFERENCE_ROW_PERCENT = 100;

export type MaxStaticBasis = 'measured' | 'estimated';

export interface MaxStatic {
  seconds: number;
  basis: MaxStaticBasis;
}

/** A max static derived from BOLT, clearly marked as an estimate. */
export const estimatedMaxStatic = (boltSeconds: number): MaxStatic => ({
  seconds: estimateMaxStaticFromBolt(boltSeconds),
  basis: 'estimated',
});

export const measuredMaxStatic = (seconds: number): MaxStatic => ({
  seconds,
  basis: 'measured',
});

export interface PhaseEligibility {
  phase: number;
  eligible: boolean;
  /** Longest hold this phase's table demands. */
  longestHoldSeconds: number;
  /**
   * The value measured max was compared against. For CO2 this is the hold
   * ceiling; for O2 it is the phase's 100% row (the entry requirement).
   */
  thresholdSeconds: number;
  reason?: string;
}

/**
 * Whether a phase is appropriate, given a measured or estimated max static.
 *
 * CO2: the constant hold must sit at or below CO2_HOLD_FRACTION_CEILING of max.
 * O2:  measured max must reach the phase's 100% row (see O2_REFERENCE_ROW_PERCENT).
 */
export const evaluatePhase = (
  phase: number,
  type: ApneaTableType,
  maxStatic: MaxStatic
): PhaseEligibility => {
  const longestHoldSeconds = maxHoldSeconds(phase, type);

  if (longestHoldSeconds === 0) {
    return {
      phase,
      eligible: false,
      longestHoldSeconds,
      thresholdSeconds: 0,
      reason: `Phase ${phase} does not exist.`,
    };
  }

  if (type === 'co2') {
    const thresholdSeconds = Math.floor(maxStatic.seconds * CO2_HOLD_FRACTION_CEILING);
    if (longestHoldSeconds > thresholdSeconds) {
      const pct = Math.round(CO2_HOLD_FRACTION_CEILING * 100);
      return {
        phase,
        eligible: false,
        longestHoldSeconds,
        thresholdSeconds,
        reason:
          `Phase ${phase} holds ${formatSeconds(longestHoldSeconds)}, above your `
          + `${pct}% CO2 ceiling of ${formatSeconds(thresholdSeconds)}.`,
      };
    }
    return { phase, eligible: true, longestHoldSeconds, thresholdSeconds };
  }

  // O2: entry is gated on reaching the phase's own 100% row.
  const thresholdSeconds = phaseEntrySeconds(phase);
  if (maxStatic.seconds < thresholdSeconds) {
    return {
      phase,
      eligible: false,
      longestHoldSeconds,
      thresholdSeconds,
      reason:
        `Phase ${phase} is built for a ${formatSeconds(thresholdSeconds)} max static. `
        + `Yours is ${formatSeconds(maxStatic.seconds)}.`,
    };
  }
  return { phase, eligible: true, longestHoldSeconds, thresholdSeconds };
};

/**
 * Max static required to enter an O2 phase: the hold that phase's table treats as
 * 100% of its reference.
 *
 * In the manual each phase's O2 table starts at (phase + 3) x 15s and steps 15s
 * per set, so the 100% row — the 2:00 reference — is set 5 in phase 1 and moves
 * one set earlier each phase. Read directly off the data as the 5th distinct hold
 * value counting from the phase's own start, which is exactly the row printed at
 * 100% for phase 1 and rises 15s per phase thereafter.
 */
export const phaseEntrySeconds = (phase: number): number => {
  const table = getNauiTable(phase, 'o2');
  if (!table) return Number.POSITIVE_INFINITY;
  // Phase 1 -> 2:00 (120s), phase 8 -> 3:45 (225s): shortest hold + 4 x 15s step.
  const shortest = Math.min(...table.sets.map(s => s.holdSeconds));
  return shortest + 4 * O2_PHASE_STEP_SECONDS;
};

/** Every NAUI O2 table steps by 15 seconds per set. */
export const O2_PHASE_STEP_SECONDS = 15;

/**
 * The highest phase the diver may run. Returns null when even phase 1 is beyond
 * them — a real and expected state for a new trainee, not an error.
 */
export const highestEligiblePhase = (
  type: ApneaTableType,
  maxStatic: MaxStatic
): number | null => {
  const eligible = NAUI_PHASES
    .map(p => evaluatePhase(p.phase, type, maxStatic))
    .filter(e => e.eligible);
  return eligible.length > 0 ? Math.max(...eligible.map(e => e.phase)) : null;
};

export const eligiblePhases = (
  type: ApneaTableType,
  maxStatic: MaxStatic
): PhaseEligibility[] => NAUI_PHASES.map(p => evaluatePhase(p.phase, type, maxStatic));

/**
 * Recommended starting phase. Deliberately conservative: one below the ceiling
 * where possible, and never above phase 1 while the max static is only estimated.
 */
export const recommendedStartPhase = (
  type: ApneaTableType,
  maxStatic: MaxStatic
): number | null => {
  const highest = highestEligiblePhase(type, maxStatic);
  if (highest === null) return null;
  if (maxStatic.basis === 'estimated') return 1;
  return Math.max(1, highest - 1);
};

export interface ResolvedTable {
  phase: number;
  type: ApneaTableType;
  sets: NauiTableSet[];
  totalSeconds: number;
  longestHoldSeconds: number;
}

/**
 * Resolve a phase into a runnable table. Throws only for a phase that does not
 * exist; eligibility is a separate, non-throwing check so callers can explain a
 * refusal rather than crash.
 */
export const resolveTable = (phase: number, type: ApneaTableType): ResolvedTable => {
  const table = getNauiTable(phase, type);
  if (!table) {
    throw new RangeError(`No NAUI ${type.toUpperCase()} table for phase ${phase}`);
  }
  return {
    phase,
    type,
    sets: table.sets,
    totalSeconds: table.sets.reduce((sum, s) => sum + s.ventSeconds + s.holdSeconds, 0),
    longestHoldSeconds: Math.max(...table.sets.map(s => s.holdSeconds)),
  };
};

/**
 * The "no-contraction" table: hold only to the first contraction, then release.
 * Holds are user-ended, so only the rest interval and round count are prescribed.
 */
export interface NoContractionTable {
  rounds: number;
  restSeconds: number;
}

export const noContractionTable = (rounds = 10): NoContractionTable => ({
  rounds: clamp(rounds, 8, 12),
  restSeconds: 60,
});

/** Apnea walk: 3 reps, 2:00 rest, distance logged rather than time. */
export interface ApneaWalkPlan {
  reps: number;
  restSeconds: number;
  breatheUpSeconds: number;
}

export const apneaWalkPlan = (reps = 3): ApneaWalkPlan => ({
  reps: clamp(reps, 1, 6),
  restSeconds: 120,
  breatheUpSeconds: 180,
});

export const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

/** `m:ss`, for display and for parsing round-trips. */
export const formatSeconds = (totalSeconds: number): string => {
  const safe = Math.max(0, Math.round(totalSeconds));
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

/**
 * Parse a user-entered duration. Accepts `m:ss`, `mm:ss`, or bare seconds, so a
 * hand-entered time (§4.2.1 of the plan) can be typed either way.
 * Returns null on anything unparseable — callers surface that, never guess.
 */
export const parseDuration = (input: string): number | null => {
  const trimmed = input.trim();
  if (trimmed === '') return null;

  const colon = trimmed.match(/^(\d{1,3}):([0-5]\d)$/);
  if (colon) {
    return Number(colon[1]) * 60 + Number(colon[2]);
  }

  const bare = trimmed.match(/^\d{1,4}$/);
  if (bare) {
    return Number(bare[0]);
  }

  return null;
};

/**
 * Plausibility check for a hand-entered time. A mistyped max static would
 * re-baseline the whole plan, so an implausible value is flagged for
 * confirmation rather than silently accepted or silently rejected.
 */
export interface PlausibilityCheck {
  plausible: boolean;
  reason?: string;
}

export const checkHoldPlausibility = (
  seconds: number,
  history: number[]
): PlausibilityCheck => {
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return { plausible: false, reason: 'Enter a time greater than zero.' };
  }
  // Beyond the current world record for static apnea; almost certainly a typo.
  if (seconds > 900) {
    return { plausible: false, reason: `${formatSeconds(seconds)} is longer than any recorded static. Check the value.` };
  }
  if (history.length === 0) return { plausible: true };

  const best = Math.max(...history);
  if (seconds > best * 1.5 && seconds - best > 30) {
    return {
      plausible: false,
      reason:
        `${formatSeconds(seconds)} is a large jump from your best of `
        + `${formatSeconds(best)}. Confirm this is right.`,
    };
  }
  return { plausible: true };
};
